import * as React from 'react';
import {HeaderSortDirection, ReactTableState} from "./ReactTable";
import {getReactButton, getReactTableLocal} from "../../index";
import {RefObject} from "react";

interface ReactTableShellData {
    id?: string,
    rows?: any[],
    headers?: any[],
    headersCSS?: string,
    rowsCSS?: (row: any, rowIndex: number) => string,
    rowsStyle?: (row: any, rowIndex: number) => {},
    highlightSelectedRow?: number, // -1 to turn off
    hightlightSelectedRowCSS?: (row: any, rowIndex: number) => string,
    rowHasFocusCSS?: (row: any, rowIndex: number) => string,
    selectRecordWithRowClick?: boolean,
    selectRecordsWithRowDrag?: boolean,
    tableLabel?: string,
    selectColumns?: [],
    addSelectColumn?: boolean,
    selectColumnIndex?: number, // -1 to add to the end
    toolBarDisplayHeader?: boolean, // If toolBarDisplayHeader and toolBarDisplayFooter are false then there is no limit, all rows will display
    toolBarDisplayFooter?: boolean,
    toolBarDisplayCompact?: boolean,
    toolBarDisplayTotalRowsSelected?: boolean,
    toolBarPageNumber?: number,
    toolBarNumToDisplay?: number,
    toolBarPrintListOnClick?: () => void,
    toolBarExportListOnClick?: () => void,
    toolBarManageColumnsOnClick?: () => void,
    toolBarStyle?: {},
    toolBarCompactStyle?: {},
    enableDragAndDrop?: boolean,
    sortColumnIndex?: number, // -1 to add to the end
    onDropEvent?: (event: any) => void,
    onSortEvent?: () => void,
    css?: string,
    style?: {},
    divTableContainerCSS?: string,
    tableStyle?: {},
    onRefresh?: () => {},
    onToolBarRefresh?: () => {},
    disableID?: string,
    refreshID?: string,
    toolBarRefreshID?: string,
    disabled?: boolean,
    disableHeaderScrolling?: boolean,
    onSelectCellClick?: (rowOBJ: any) => void,
    selectHeaderClick?: () => void,
    clearSelectionClick?: () => void,
    addDeleteColumn?: boolean,
    deleteButtonStyle?: (row: any, rowIndex: number) => string,
    deleteButtonTabIndex?: null,
    deleteColumnIndex?: number, // -1 to add to the end
    onDelete?: (rowObj: any, rowIndex: number) => void,
    lockedRows?: [],
    disableRow?: (row: any, rowIndex: number) => boolean,
    disableDelete?: (row: any, rowIndex: number) => boolean,
    noRecordsSelectedText?: string,
    addCustomFilterOptions?: (storedData?: any, filteredRows?: any[]) => {},
    customFilterCSS?: string,
    customFilter?: (row: any) => boolean,
    addCustomToolBarOptions?: (storedData?: any) => {},
    isSelectedKey?: string,
    customSortIndexKey?: string,
    onRowDoubleClick?: (rowOBJ: any) => void,
    clearButtonTabIndex?: null,
    divSAYTWindowCSS?: string,
    divTableShellCSS?: string,
    divTableContainerShellCSS?: string,
    emptyRowCSS?: string,
    isInitialLoad?: () => boolean,
    overrideInitialSortColumns?: string,
    overrideSortID?: string,
    overrideSortName?: string,
    overrideRowID? : (row: any, rowIndex: number, overallIndex: number) => string,
    overrideRowName? : (row: any, rowIndex: number, overallIndex: number) => string,
    deleteButtonID? : (row: any, rowIndex: number, overallIndex: number) => string,
    deleteButtonName? : (row: any, rowIndex: number, overallIndex: number) => string,
    selectCheckBoxID? : (row: any, rowIndex: number, overallIndex: number) => string,
    selectCheckBoxName? : (row: any, rowIndex: number, overallIndex: number) => string,
    overrideTableEntriesText? : string,
    overrideScrollIntoView?: (element: any, row: any, rowIndex: number) => void,
    toolBarDisplayTotalCSS?: string,
    toolBarDisplayTotalStyle?: {},
    onSelectionChange?: (totalSelected: number) => void,
    overrideToolBarInfoText? : (totalRows: number, startPos: number, endPos: number, totalPages: number, page: number) => {},
    onTableScroll?: (scrollTop: number, scrollHeight: number, clientHeight: number, selectIndex: number, numberOfRowsDisplayed: number) => void,
    onKeyDown?: (rowOBJ: any, rowIndex: number, keyPressed: string, bShift: boolean, bCTRL: boolean, bALT: boolean) => null,
    onKeyUp?: (rowOBJ: any, rowIndex: number, keyPressed: string, bShift: boolean, bCTRL: boolean, bALT: boolean) => null,
    stopPropagationOnKeyPress?: (rowOBJ: any, rowIndex: number, keyPressed: string, bOnKeyUp: boolean, bShift: boolean, bCTRL: boolean, bALT: boolean) => boolean,
    onDropHeaderEvent?: (header: any, dragOverHeader: any) => void,
    deleteColumnHeader?: string,
    toolBarDisablePaging?: boolean,
    toolBarDisplayTotal?: () => number,
}

interface ReactTableShellState {
    highlightSelectedRow: number,
    toolBarPageNumber: number,
    toolBarPageNumberTyped: number,
    toolBarNumToDisplay: number,
    rows: any[], // All Rows
    filteredRows: any[], // Displayed Rows
    disabled: boolean,
    headers: any[],
}

// Shell React Table accessible to users
export class ReactTableShell extends React.Component<ReactTableShellData> {
    useHighlightSelectedRow: boolean;
    inputRef: RefObject<any>;

    constructor(props: ReactTableShellData) {
        super(props);

        // Binding is necessary to allow these functions access to the State data
        this.onSelectCellClick = this.onSelectCellClick.bind(this);
        this.selectAll = this.selectAll.bind(this);
        this.unselectAll = this.unselectAll.bind(this);
        this.onDisableClick = this.onDisableClick.bind(this);
        this.toolBarUpdate = this.toolBarUpdate.bind(this);
        this.sortUpdate = this.sortUpdate.bind(this);
        this.getTotalSelected = this.getTotalSelected.bind(this);
        this.onDrop = this.onDrop.bind(this);
        this.onDuringDrag = this.onDuringDrag.bind(this);
        this.onDuringDragHeader = this.onDuringDragHeader.bind(this);
        this.onDropHeaderEvent = this.onDropHeaderEvent.bind(this);
        this.onDelete = this.onDelete.bind(this);
        this.onRefresh = this.onRefresh.bind(this);

        this.inputRef = React.createRef();
        this.useHighlightSelectedRow = props.highlightSelectedRow >= 0;

        // Set the initial state data
        this.state = {
            rows: props.rows,
            filteredRows: this.filterTable(props.rows),
            highlightSelectedRow: props.highlightSelectedRow,
            toolBarNumToDisplay: props.toolBarNumToDisplay,
            toolBarPageNumber: props.toolBarPageNumber,
            toolBarPageNumberTyped: props.toolBarPageNumber,
            disabled: props.disabled,
            headers: props.headers,
        };
    }

    componentDidMount() {
        // Add Selection checkbox cells
        if (this.props.selectColumns.length > 0) {
            this.setState(() => {
                return {
                    rows: (this.state as ReactTableShellState).rows,
                    filteredRows: this.filterTable((this.state as ReactTableShellState).rows),
                };
            });
        }
    }

    // Update the state externally
    componentWillReceiveProps(props: ReactTableShellData) {
        this.setState({
            disabled: props.disabled,
            rows: props.rows,
            filteredRows: this.filterTable(props.rows),
        });
    }

    onSelectCellClick(row: any, shiftKey: boolean, ctrlKey: boolean, applySelection: boolean, lastSelectedRowIndex: number) {
        const rowIndex = this.indexOfRow(row);

        // If the table is Disabled don't allow row selecting
        if(!(this.state as ReactTableShellState).disabled) {
            if (rowIndex >= 0) {
                const displayedRowIndex = this.indexOfFilteredRow(row);
                const startIndex = (lastSelectedRowIndex > -1 && lastSelectedRowIndex < displayedRowIndex) ? lastSelectedRowIndex : displayedRowIndex;
                const endIndex = (lastSelectedRowIndex > displayedRowIndex) ? lastSelectedRowIndex : displayedRowIndex;

                this.setState(() => {
                    if(this.props.selectRecordWithRowClick) {
                        if(shiftKey) {
                            (this.state as ReactTableShellState).filteredRows.map((r, index) => {
                                if(displayedRowIndex == index) {
                                    const rowIndex2 = this.indexOfRow(r);
                                    if(rowIndex2 >= 0) {
                                        (this.state as ReactTableShellState).rows[rowIndex2][this.props.isSelectedKey] = true;
                                    }
                                } else {
                                    if(lastSelectedRowIndex > -1 && index >= startIndex && index <= endIndex) {
                                        const rowIndex2 = this.indexOfRow(r);
                                        if(rowIndex2 >= 0) {
                                            (this.state as ReactTableShellState).rows[rowIndex2][this.props.isSelectedKey] = true;
                                        }
                                    }
                                }
                            });
                        } else if(ctrlKey) {
                            const setSelected = ((this.state as ReactTableShellState).rows[rowIndex][this.props.isSelectedKey]) ? !(this.state as ReactTableShellState).rows[rowIndex][this.props.isSelectedKey] : true;
                            (this.state as ReactTableShellState).rows[rowIndex][this.props.isSelectedKey] = setSelected;
                        } else{
                            (this.state as ReactTableShellState).rows.map((r, index) => {r[this.props.isSelectedKey] = (index == rowIndex)});
                        }
                    } else {
                        let setSelected = true;
                        if(null != (this.state as ReactTableShellState).rows[rowIndex][this.props.isSelectedKey] && 'undefined' != (this.state as ReactTableShellState).rows[rowIndex][this.props.isSelectedKey]) {
                            if(applySelection) {
                                setSelected = !(this.state as ReactTableShellState).rows[rowIndex][this.props.isSelectedKey];
                            } else {
                                setSelected = (this.state as ReactTableShellState).rows[rowIndex][this.props.isSelectedKey];
                            }
                        };
                        (this.state as ReactTableShellState).rows[rowIndex][this.props.isSelectedKey] = setSelected;
                    }

                    return {
                        rows: (this.state as ReactTableShellState).rows,
                        filteredRows: this.filterTable((this.state as ReactTableShellState).rows),
                        highlightSelectedRow: (this.state as ReactTableShellState).highlightSelectedRow,
                        toolBarPageNumber: (this.state as ReactTableShellState).toolBarPageNumber,
                        toolBarPageNumberTyped: (this.state as ReactTableShellState).toolBarPageNumberTyped,
                        toolBarNumToDisplay: (this.state as ReactTableShellState).toolBarNumToDisplay,
                    }});
            }
        }

        if(this.props.onSelectCellClick) {
            this.props.onSelectCellClick(row);
        }
    }

    onDisableClick(event: any) {
        const isChecked = $(event.currentTarget).is(":checked");
        this.setState({
            disabled: isChecked
        });
    }

    getTotalSelected() {
        let count = 0;
        for (let i = 0; i < (this.state as ReactTableShellState).rows.length; i++) {
            const row = (this.state as ReactTableShellState).rows[i];
            if(null != row.isSelected && row.isSelected) {
                count++;
            }
        }
        if(null != this.props.onSelectionChange) {
            this.props.onSelectionChange(count);
        }
        return count;
    }

    onRefresh() {
        // Call onRefresh if the function exists
        if(null != this.props.onRefresh) {
            this.props.onRefresh();
        }

        // When using Drag and Drop Update the Sort Indexes
        if(this.props.enableDragAndDrop) {
            (this.state as ReactTableShellState).rows.map((row, rowIndex) => {
                row[this.props.customSortIndexKey] = rowIndex;
            });
        }

        // Update the current state
        if(null != this.props.isInitialLoad && this.props.isInitialLoad()) {
            this.setState(() => ({
                rows: (this.state as ReactTableShellState).rows,
                filteredRows: this.filterTable((this.state as ReactTableShellState).rows),
            }));
        } else {
            this.setState(() => ({
                rows: (this.state as ReactTableShellState).rows,
                filteredRows: this.filterTable((this.state as ReactTableShellState).rows),
                toolBarPageNumber: 1,
                toolBarPageNumberTyped: 1,
                highlightSelectedRow: ((this.useHighlightSelectedRow) ? 0 : -1)
            }));
        }
    }

    // Get the index of the row in the rows array
    indexOfRow(row: any) {
        for(let i = 0; i < (this.state as ReactTableShellState).rows.length; i++) {
            if(row == (this.state as ReactTableShellState).rows[i] as any) {
                return i;
            }
        }

        return -1;
    }

    toolBarUpdate(props: ReactTableState) {
        this.setState(() => ({
            toolBarPageNumber: props.toolBarPageNumber,
            toolBarPageNumberTyped: props.toolBarPageNumberTyped,
            highlightSelectedRow: props.highlightSelectedRow,
            toolBarNumToDisplay: props.toolBarNumToDisplay,
        }));
    }

    sortUpdate(props: ReactTableState, onLoad: boolean) {
        this.setState(() => ({
            filteredRows: this.filterTable(props.rows),
        }));
        this.toolBarUpdate(props);
    }

    // Get the index of the row in the rows array
    indexOfFilteredRow(row: any) {
        for(let i = 0; i < (this.state as ReactTableShellState).filteredRows.length; i++) {
            if(row == (this.state as ReactTableShellState).filteredRows[i] as any) {
                return i;
            }
        }

        return -1;
    }

    // Unselect all records in the rows array
    unselectAll() {
        // Do nothing if the table is Disabled
        if(!(this.state as ReactTableShellState).disabled) {
            // Unselect all of the rows (even rows filtered and rows off page)
            for(let i = 0; i < (this.state as ReactTableShellState).rows.length; i++) {
                (this.state as ReactTableShellState).rows[i][this.props.isSelectedKey] = false;
            }

            // Update the current state
            this.setState({
                rows: (this.state as ReactTableShellState).rows,
                filteredRows: this.filterTable((this.state as ReactTableShellState).rows),
                toolBarPageNumber: (this.state as ReactTableShellState).toolBarPageNumber,
                toolBarPageNumberTyped: (this.state as ReactTableShellState).toolBarPageNumberTyped,
                toolBarNumToDisplay: (this.state as ReactTableShellState).toolBarNumToDisplay,
            });

            // Call the clearSelectionClick function if it exists, The "Clear" button comes with the Total Rows Selected tool bar
            if(this.props.clearSelectionClick) {
                this.props.clearSelectionClick();
            }
        }
    }

    // Select/unselect all displayed rows
    selectAll() {
        // Do nothing if the table is Disabled
        if (!(this.state as ReactTableShellState).disabled) {

            let startingIndex = 0;
            let endingIndex = (this.state as ReactTableShellState).filteredRows.length;
            if ((this.state as ReactTableShellState).toolBarNumToDisplay > 0) {
                startingIndex = (((this.state as ReactTableShellState).toolBarPageNumber - 1) * (this.state as ReactTableShellState).toolBarNumToDisplay);
                endingIndex = startingIndex + (this.state as ReactTableShellState).toolBarNumToDisplay;
                if (endingIndex > (this.state as ReactTableShellState).filteredRows.length) {
                    endingIndex = (this.state as ReactTableShellState).filteredRows.length;
                }
            }

            // Use the first row currently displayed (using the startingIndex) to determine if we should mark all displayed rows as selected or unselected
            const markAsSelected = ((this.state as ReactTableShellState).filteredRows.length > 0) ? !(((this.state as ReactTableShellState).filteredRows[startingIndex][this.props.isSelectedKey]) ? (this.state as ReactTableShellState).filteredRows[startingIndex][this.props.isSelectedKey] : false) : false;

            // Mark all of the rows displayed as Selected/Unselected
            for (let i = startingIndex; i < endingIndex; i++) {
                const rowIndex = this.indexOfRow((this.state as ReactTableShellState).filteredRows[i]);
                if (rowIndex >= 0) {
                    (this.state as ReactTableShellState).rows[rowIndex][this.props.isSelectedKey] = markAsSelected;
                }
            }

            // Update the current state
            this.setState({
                rows: (this.state as ReactTableShellState).rows,
                filteredRows: this.filterTable((this.state as ReactTableShellState).rows),
                toolBarPageNumber: (this.state as ReactTableShellState).toolBarPageNumber,
                toolBarPageNumberTyped: (this.state as ReactTableShellState).toolBarPageNumberTyped,
                toolBarNumToDisplay: (this.state as ReactTableShellState).toolBarNumToDisplay
            });

            // Call the selectHeaderClick function if it exists
            if (this.props.selectHeaderClick) {
                this.props.selectHeaderClick();
            }
        }
    }

    // Function to run while rows are being dragged
    onDuringDrag(event: any, row: any, dragOverRow: any) {
        const currentIndex = (this.state as ReactTableShellState).rows.indexOf(row);
        const dragIndex = (this.state as ReactTableShellState).rows.indexOf(dragOverRow);
        if (currentIndex > -1 && dragIndex > -1) {
            (this.state as ReactTableShellState).rows.splice(currentIndex, 1);
            (this.state as ReactTableShellState).rows.splice(dragIndex, 0, row);

            // Update the current state
            this.setState(() => {
                return {
                    rows: (this.state as ReactTableShellState).rows,
                    filteredRows: this.filterTable((this.state as ReactTableShellState).rows),
                };
            });
        }
    }

    // Function to run when row is dropped
    onDrop(event: any) {
        // Call the onDropEvent function if it exists
        if (null != this.props.onDropEvent) {
            this.props.onDropEvent(event);
        }
    }

    // Function to run while headers are being dragged
    onDuringDragHeader(event: any, header: any, dragOverHeader: any, headerCellRef: RefObject<any>) {
        const currentIndex = (this.state as ReactTableShellState).headers.indexOf(header);
        const dragIndex = (this.state as ReactTableShellState).headers.indexOf(dragOverHeader);
        if (currentIndex > -1 && dragIndex > -1) {
            if ($(headerCellRef.current)[0].getBoundingClientRect().left + 30 > event.clientX) {
                (this.state as ReactTableShellState).headers.splice(currentIndex, 1);
                (this.state as ReactTableShellState).headers.splice(dragIndex, 0, header);

                // Update the current state
                this.setState(() => {
                    return {
                        headers: (this.state as ReactTableShellState).headers,
                    };
                });
            }
        }
    }

    // Function to run when row is dropped
    onDropHeaderEvent(event: any, header: any, dragOverHeader: any) {
        // Call the onDropEvent function if it exists
        if (null != this.props.onDropHeaderEvent) {
            this.props.onDropHeaderEvent(header, dragOverHeader);
        }
    }

    // Function to run when row is deleted
    onDelete(filterRowIndex: number) {
        const row = (this.state as ReactTableShellState).filteredRows[filterRowIndex];
        const rowIndex = this.indexOfRow(row);
        if(rowIndex >= -1) {
            (this.state as ReactTableShellState).rows.splice(rowIndex, 1);

            // Update the current state
            this.setState({
                rows: (this.state as ReactTableShellState).rows,
                filteredRows: this.filterTable((this.state as ReactTableShellState).rows),
            });

            // Call the onDelete function if it exists
            if(null != this.props.onDelete) {
                this.props.onDelete(row, rowIndex);
            }

            const totalRows = (this.state as ReactTableState).filteredRows.length;
            const startPos = ((this.state as ReactTableState).toolBarPageNumber - 1) * (this.state as ReactTableState).toolBarNumToDisplay;
            const prevPage = (((this.state as ReactTableShellState).toolBarPageNumber) > 1) ? (this.state as ReactTableShellState).toolBarPageNumber -1 : (this.state as ReactTableShellState).toolBarPageNumber;
            const updatedPageNumber = ((totalRows - 1) <= startPos) ? prevPage : -1;
            if(updatedPageNumber > -1) {
                this.setState({
                    toolBarPageNumber: updatedPageNumber,
                    toolBarPageNumberTyped: updatedPageNumber,
                    highlightSelectedRow: ((this.useHighlightSelectedRow) ? 0 : -1)
                });
            }

            return updatedPageNumber;
        }
    }

    // Filter the rows using the customFilter if the customFilter exists
    searchRow(row: any[]) {
        let skipCustomFilter = true;

        // Filter using the customFilter if it exists
        if(null != this.props.customFilter) {
            skipCustomFilter = this.props.customFilter(row);
        }

        return skipCustomFilter;
    }

    // Filter the rows if the customFilter exists
    filterTable(rows: any[]) {
        return rows.filter(row => this.searchRow(row));
    }

    getHeaders() {
        const currentHeaders = (this.state as ReactTableShellState).headers;
        const overrideInitialSort = (null != this.props.isInitialLoad && this.props.isInitialLoad()) && (null != this.props.overrideInitialSortColumns) && (this.props.overrideInitialSortColumns.length > 0);
        if (overrideInitialSort) {
            const newHeaders: any[] = JSON.parse(this.props.overrideInitialSortColumns);

            if (currentHeaders.length === newHeaders.length) {
                for (let i = 0; i < currentHeaders.length; i++) {
                    currentHeaders[i].sortOrder = newHeaders[i].sortOrder;
                    currentHeaders[i].sortDirection = newHeaders[i].sortDirection;
                }
            }
        }
        return currentHeaders;
    }

    render() {
        // Set the customSortIndexKey values using the current indexes (every time the table is rendered)
        // This prevents issues that may occur if the customSortIndexKey object doesn't exist in the data
        if(this.props.enableDragAndDrop) {
            (this.state as ReactTableShellState).rows.map((row, rowIndex) => {
                row[this.props.customSortIndexKey] = rowIndex;
            });
        }

        // Render the table
        return <div className={this.props.css} style={this.props.style}>
            <div>
                {<table className="categoryAllHide"><tr>
                    <td><input
                        id={this.props.disableID}
                        type="checkbox"
                        onChange={this.onDisableClick}
                        checked={(this.state as ReactTableShellState).disabled}
                    /></td>
                    <td>
                        HIDDEN DISABLE BUTTON
                    </td>
                </tr></table>
                }
                {getReactButton({id: this.props.refreshID, css: "categoryAllHide", onClick: this.onRefresh, text: "HIDDEN REFRESH BUTTON"})}
            </div>
            {getReactTableLocal({
                id: this.props.id + '_table',
                rows: (this.state as ReactTableShellState).rows,
                filteredRows: (this.state as ReactTableShellState).filteredRows,
                headers: this.getHeaders(),
                headersCSS: this.props.headersCSS,
                rowsCSS: this.props.rowsCSS,
                rowsStyle: this.props.rowsStyle,
                highlightSelectedRow: (this.state as ReactTableShellState).highlightSelectedRow,
                tableLabel: this.props.tableLabel,
                selectColumns: this.props.selectColumns,
                onSelectCellClick: this.onSelectCellClick,
                addSelectColumn: this.props.addSelectColumn,
                selectColumnIndex: this.props.selectColumnIndex,
                toolBarDisplayHeader: this.props.toolBarDisplayHeader,
                toolBarDisplayFooter: this.props.toolBarDisplayFooter,
                toolBarDisplayCompact: this.props.toolBarDisplayCompact,
                toolBarDisplayTotalRowsSelected: this.props.toolBarDisplayTotalRowsSelected,
                toolBarPageNumber: (this.state as ReactTableShellState).toolBarPageNumber,
                toolBarNumToDisplay: (this.state as ReactTableShellState).toolBarNumToDisplay,
                toolBarPrintListOnClick: this.props.toolBarPrintListOnClick,
                toolBarExportListOnClick: this.props.toolBarExportListOnClick,
                toolBarManageColumnsOnClick: this.props.toolBarManageColumnsOnClick,
                toolBarStyle: this.props.toolBarStyle,
                toolBarCompactStyle: this.props.toolBarCompactStyle,
                enableDragAndDrop: this.props.enableDragAndDrop,
                sortColumnIndex: this.props.sortColumnIndex,
                css: this.props.divTableContainerCSS,
                style: this.props.tableStyle,
                tableStyle: this.props.tableStyle,
                toolBarUpdate: this.toolBarUpdate,
                sortUpdate: this.sortUpdate,
                onRefresh: this.props.onRefresh,
                toolBarRefreshID: this.props.toolBarRefreshID,
                onToolBarRefresh: this.props.onToolBarRefresh,
                refreshID: this.props.refreshID,
                disabled: (this.state as ReactTableShellState).disabled,
                disableHeaderScrolling: this.props.disableHeaderScrolling,
                selectHeaderClick: this.selectAll,
                clearSelectionClick: this.unselectAll,
                addDeleteColumn: this.props.addDeleteColumn,
                deleteButtonStyle: this.props.deleteButtonStyle,
                deleteColumnIndex: this.props.deleteColumnIndex,
                lockedRows: this.props.lockedRows,
                disableRow: this.props.disableRow,
                disableDelete: this.props.disableDelete,
                toolBarNumToDisplayTyped: (this.state as ReactTableShellState).toolBarNumToDisplay,
                toolBarPageNumberTyped: (this.state as ReactTableShellState).toolBarPageNumberTyped,
                noRecordsSelectedText: this.props.noRecordsSelectedText,
                addCustomFilterOptions: this.props.addCustomFilterOptions,
                customFilterCSS: this.props.customFilterCSS,
                addCustomToolBarOptions: this.props.addCustomToolBarOptions,
                deleteButtonTabIndex: this.props.deleteButtonTabIndex,
                onDropEvent: this.onDrop,
                onSortEvent: this.props.onSortEvent,
                onDuringDragEvent: this.onDuringDrag,
                onDelete: this.onDelete,
                selectRecordWithRowClick: this.props.selectRecordWithRowClick,
                selectRecordsWithRowDrag: this.props.selectRecordsWithRowDrag,
                isSelectedKey: this.props.isSelectedKey,
                hightlightSelectedRowCSS: this.props.hightlightSelectedRowCSS,
                rowHasFocusCSS: this.props.rowHasFocusCSS,
                onRowDoubleClick: this.props.onRowDoubleClick,
                getTotalSelected: this.getTotalSelected,
                clearButtonTabIndex: this.props.clearButtonTabIndex,
                divSAYTWindowCSS: this.props.divSAYTWindowCSS,
                divTableShellCSS: this.props.divTableShellCSS,
                divTableContainerShellCSS: this.props.divTableContainerShellCSS,
                emptyRowCSS: this.props.emptyRowCSS,
                isInitialLoad: this.props.isInitialLoad,
                overrideSortID: this.props.overrideSortID,
                overrideSortName: this.props.overrideSortName,
                overrideRowID: this.props.overrideRowID,
                overrideRowName: this.props.overrideRowName,
                deleteButtonID: this.props.deleteButtonID,
                deleteButtonName: this.props.deleteButtonName,
                selectCheckBoxID: this.props.selectCheckBoxID,
                selectCheckBoxName: this.props.selectCheckBoxName,
                overrideTableEntriesText: this.props.overrideTableEntriesText,
                overrideScrollIntoView: this.props.overrideScrollIntoView,
                onSelectionChange: this.props.onSelectionChange,
                toolBarDisplayTotalCSS: this.props.toolBarDisplayTotalCSS,
                toolBarDisplayTotalStyle: this.props.toolBarDisplayTotalStyle,
                overrideToolBarInfoText: this.props.overrideToolBarInfoText,
                onTableScroll: this.props.onTableScroll,
                onKeyDown: this.props.onKeyDown,
                onKeyUp: this.props.onKeyUp,
                stopPropagationOnKeyPress: this.props.stopPropagationOnKeyPress,
                onDuringDragHeader: this.onDuringDragHeader,
                onDropHeaderEvent: this.onDropHeaderEvent,
                deleteColumnHeader: this.props.deleteColumnHeader,
                toolBarDisablePaging: this.props.toolBarDisablePaging,
                toolBarDisplayTotal: this.props.toolBarDisplayTotal,
            })}
        </div>;
    }
}
